import { Button, Input, Spinner, Tab, Tabs } from '@heroui/react';
import { Image as ImageIcon, Images, Search, Upload } from 'lucide-react';
import React, { FC, useEffect, useState } from 'react';

import {
  AiAssetInput,
  useAdGeneratorSettings,
  useGetAdAssets,
  useSearchStockPhotos,
} from '../../../../../queries';
import { useAssetUpload } from '../../../hooks';
import { useAdsGeneratorStore } from '../../../model/store';
import { ComposerHint, FieldGroup, FieldTitle, PickerGrid, PickerTile, TileBadge } from './AiComposer.styled';

interface AssetPickerProps {
  selected: AiAssetInput[];
  onChange: (assets: AiAssetInput[]) => void;
  /** Fraza dopasowana do wybranego scenariusza; bank otwiera się z jej wynikami. */
  stockTerm?: string;
}

/** Zapytanie startowe, gdy użytkownik nie wybrał żadnego scenariusza. */
const FALLBACK_STOCK_TERM = 'modern hotel interior';

/** Orientacja wyników stocka dobrana do proporcji planszy. */
const canvasOrientation = (width: number, height: number): string => {
  const ratio = width / height;
  if (ratio > 1.6) return 'panoramic';
  if (ratio > 1.1) return 'landscape';
  if (ratio < 0.9) return 'portrait';
  return 'square';
};

/**
 * Wybór zdjęć, których AI może użyć w kreacji.
 *
 * Pozycje z Magnifica dokładamy jako PODGLĄDY razem z `stockId`. Licencjonowany
 * plik pobierze się dopiero po generacji i tylko dla tych zdjęć, które model
 * faktycznie wstawił — zaznaczenie trzech kandydatów nie zabiera trzech pobrań
 * z dziennej puli.
 */
export const AssetPicker: FC<AssetPickerProps> = ({ selected, onChange, stockTerm }) => {
  const scene = useAdsGeneratorStore((state) => state.scene);
  const { ai } = useAdGeneratorSettings();
  const { data: library = [], isLoading } = useGetAdAssets();
  const { inputRef, accept, isUploading, openPicker, handleFiles } = useAssetUpload();

  const [source, setSource] = useState('library');
  const [term, setTerm] = useState(stockTerm ?? FALLBACK_STOCK_TERM);
  const [submitted, setSubmitted] = useState(stockTerm ?? FALLBACK_STOCK_TERM);

  // Zmiana scenariusza przestawia wyszukiwarkę, żeby bank pokazał zdjęcia
  // pasujące do tego, co użytkownik właśnie zamówił.
  useEffect(() => {
    if (!stockTerm) return;
    setTerm(stockTerm);
    setSubmitted(stockTerm);
  }, [stockTerm]);

  const orientation = scene ? canvasOrientation(scene.width, scene.height) : undefined;
  const { data: stock, isFetching } = useSearchStockPhotos(
    { term: submitted, orientation },
    source === 'stock' && submitted.length > 1,
  );

  const isSelected = (url: string): boolean => selected.some((asset) => asset.url === url);
  const indexOf = (url: string): number => selected.findIndex((asset) => asset.url === url) + 1;

  const toggle = (asset: AiAssetInput): void => {
    if (isSelected(asset.url)) {
      onChange(selected.filter((item) => item.url !== asset.url));
      return;
    }
    if (selected.length >= ai.maxAssets) return;
    onChange([...selected, asset]);
  };

  return (
    <FieldGroup>
      <FieldTitle>{`Zdjęcia do użycia (${selected.length} z ${ai.maxAssets})`}</FieldTitle>

      {/* Źródło jako pełnowymiarowy przełącznik, a nie drobne zakładki w rogu:
          bank zdjęć jest tu równorzędną drogą do materiału, nie dodatkiem. */}
      <Tabs
        fullWidth
        size="md"
        radius="sm"
        color="primary"
        aria-label="Źródło zdjęć"
        selectedKey={source}
        onSelectionChange={(key) => setSource(String(key))}
      >
        <Tab
          key="library"
          title={(
            <span className="flex items-center gap-2">
              <ImageIcon size={16} />
              Moje obrazy
            </span>
          )}
        />
        <Tab
          key="stock"
          title={(
            <span className="flex items-center gap-2">
              <Images size={16} />
              Bank zdjęć Magnific
            </span>
          )}
        />
      </Tabs>

      {source === 'library' && (
        <>
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            hidden
            onChange={async (event) => {
              const uploaded = await handleFiles(event.target.files);
              if (uploaded[0]) {
                toggle({
                  url: uploaded[0].url,
                  width: uploaded[0].width,
                  height: uploaded[0].height,
                  label: uploaded[0].name,
                });
              }
            }}
          />
          <Button
            size="sm"
            radius="sm"
            variant="flat"
            startContent={<Upload size={16} />}
            isLoading={isUploading}
            onPress={openPicker}
          >
            Wgraj nowe zdjęcie
          </Button>

          {isLoading && <Spinner size="sm" />}

          {!isLoading && library.length === 0 && (
            <ComposerHint>Biblioteka jest pusta. Wgraj zdjęcie albo poszukaj w Magnificu.</ComposerHint>
          )}

          <PickerGrid>
            {library.map((asset) => (
              <PickerTile
                key={asset.id}
                type="button"
                $selected={isSelected(asset.url)}
                title={asset.name}
                onClick={() => toggle({
                  url: asset.url,
                  width: asset.width,
                  height: asset.height,
                  label: asset.name,
                })}
              >
                <img src={asset.url} alt="" crossOrigin="anonymous" />
                {isSelected(asset.url) && <TileBadge>{indexOf(asset.url)}</TileBadge>}
              </PickerTile>
            ))}
          </PickerGrid>
        </>
      )}

      {source === 'stock' && (
        <>
          <div className="flex gap-1">
            <Input
              size="sm"
              radius="sm"
              className="min-w-0"
              placeholder="Czego szukasz?"
              value={term}
              onValueChange={setTerm}
              onKeyDown={(event) => {
                if (event.key === 'Enter') setSubmitted(term.trim());
              }}
            />
            <Button
              size="sm"
              radius="sm"
              isIconOnly
              variant="flat"
              aria-label="Szukaj"
              isLoading={isFetching}
              onPress={() => setSubmitted(term.trim())}
            >
              <Search size={16} />
            </Button>
          </div>

          <ComposerHint>
            Zaznaczenie nic nie kosztuje. Zdjęcie pobierze się z puli dopiero wtedy, gdy AI wstawi je w kreację.
          </ComposerHint>

          <PickerGrid>
            {(stock?.items ?? []).map((photo) => (
              <PickerTile
                key={photo.id}
                type="button"
                $selected={isSelected(photo.previewUrl)}
                title={photo.title}
                onClick={() => toggle({
                  url: photo.previewUrl,
                  width: Number(photo.previewSize.split('x')[0]) || undefined,
                  height: Number(photo.previewSize.split('x')[1]) || undefined,
                  label: photo.title,
                  stockId: photo.id,
                })}
              >
                <img src={photo.previewUrl} alt="" />
                {isSelected(photo.previewUrl) && <TileBadge>{indexOf(photo.previewUrl)}</TileBadge>}
              </PickerTile>
            ))}
          </PickerGrid>
        </>
      )}
    </FieldGroup>
  );
};
